<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import SanierungsfragenElement from "@/components/sanierung/SanierungsfragenElement.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Sanierungsfragen",
  components: { SanierungsfragenElement, Ueberschrift },
  props: {
    aelterAls50: Boolean,
    fehler: Object,
  },
  setup() {
    const piniaStore = useFormStore();

    const showables = ref({
      showHelp: false,
    });

    onMounted(() => {
      piniaStore.state.gebaeudedaten.sanierungsdaten.sanierungsdatenAbgefragt = true;
    });

    return {
      showables,
      piniaStore,
    };
  },
});
</script>

<template>
  <ueberschrift titel="Angaben zur Sanierung" />
  <div class="o-grid">
    <div class="o-grid__cell o-grid__cell--4-1024 o-grid__cell--12-360">
      <p v-if="aelterAls50" class="u-text-align-special u-mar-0">
        Welche der hier aufgelisteten Elemente wurden unter fachmännischer Ausführung in welchem Jahr komplett erneuert?
      </p>
      <p v-if="!aelterAls50" class="u-text-align-special u-mar-0">
        Die Dacheindeckung wurde unter fachmännischer Ausführung komplett erneuert
      </p>
    </div>
    <div class="o-grid__cell o-grid__cell--6-1024 o-grid__cell--12-360">
      <SanierungsfragenElement
        v-model:jahrChecked="piniaStore.state.gebaeudedaten.sanierungsdaten.dachbedeckung"
        v-model:jahr="piniaStore.state.gebaeudedaten.sanierungsdaten.dachbedeckungJahr"
        :name="'Dachdeckung'"
        :infoText="'An der Dachkonstruktion, der Dachhaut und der Dachdeckung wurden Renovierungsarbeiten durchgeführt.'"
        :fehler="fehler"
        :sanierungsfrage="{
          nameCheckbox: 'frage1Checkbox',
          nameInput: 'frage1Input',
        }"
        :schmales-label="!(aelterAls50 || piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt)"
      ></SanierungsfragenElement>

      <SanierungsfragenElement
        v-if="aelterAls50 || piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt"
        v-model:jahrChecked="piniaStore.state.gebaeudedaten.sanierungsdaten.rohreGebaeude"
        v-model:jahr="piniaStore.state.gebaeudedaten.sanierungsdaten.rohreGebaeudeJahr"
        :name="'Rohre der Wasserver-/Entsorgung im Gebäude'"
        :infoText="
          'Die Grundvoraussetzung ist die komplette Erneuerung der Zu- und Ableitungsrohre inklusive der ' +
          'dazugehörigen Armaturen.'
        "
        :fehler="fehler"
        :sanierungsfrage="{
          nameCheckbox: 'frage2Checkbox',
          nameInput: 'frage2Input',
        }"
      ></SanierungsfragenElement>

      <SanierungsfragenElement
        v-if="aelterAls50 || piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt"
        v-model:jahrChecked="piniaStore.state.gebaeudedaten.sanierungsdaten.rohreAusserhalb"
        v-model:jahr="piniaStore.state.gebaeudedaten.sanierungsdaten.rohreAusserhalbJahr"
        :name="'Rohre der Wasserver-/Entsorgung außerhalb des Gebäudes'"
        :infoText="
          'Abwasserführende Rohre außerhalb des Gebäudes wurden erneuert bzw. es wurde in den letzten 10 ' +
          'Jahren eine Dichtheitsprüfung nachweislich durchgeführt'
        "
        :fehler="fehler"
        :sanierungsfrage="{
          nameCheckbox: 'frage3Checkbox',
          nameInput: 'frage3Input',
        }"
      ></SanierungsfragenElement>

      <SanierungsfragenElement
        v-if="aelterAls50 || piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt"
        v-model:jahrChecked="piniaStore.state.gebaeudedaten.sanierungsdaten.leitungenElektrik"
        v-model:jahr="piniaStore.state.gebaeudedaten.sanierungsdaten.leitungenElektrikJahr"
        :name="'Elektrische Leitungen und Installationen'"
        :infoText="
          'Die elektrischen Leitungen des gesamten Gebäudes wurden in einen neuwertigen Zustand versetzt. ' +
          'Dazu gehören auch alle Schalter und Sicherungseinrichtungen des Hauses.'
        "
        :fehler="fehler"
        :sanierungsfrage="{
          nameCheckbox: 'frage4Checkbox',
          nameInput: 'frage4Input',
        }"
      ></SanierungsfragenElement>

      <SanierungsfragenElement
        v-if="aelterAls50 || piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt"
        v-model:jahrChecked="piniaStore.state.gebaeudedaten.sanierungsdaten.leitungenHeizung"
        v-model:jahr="piniaStore.state.gebaeudedaten.sanierungsdaten.leitungenHeizungJahr"
        :name="'Heizungsleitungen'"
        :infoText="
          'Die Heizungsanlage des Gebäudes wurde komplett saniert. Dazu gehört, dass Heizkessel und ' +
          'Brenner nachweislich rundum ereuert wurden.'
        "
        :fehler="fehler"
        :sanierungsfrage="{
          nameCheckbox: 'frage5Checkbox',
          nameInput: 'frage5Input',
        }"
      ></SanierungsfragenElement>

      <div class="gev19-infobox--blue" v-if="!piniaStore.state.isMakler && aelterAls50">
        <strong>Hinweis:</strong> Haben die Maßnahmen in unterschiedlichen Jahren stattgefunden oder sind Sie sich beim
        Ausfüllen unsicher, rufen Sie uns gerne an. Sie erreichen unser Beratungsteam unter
        <strong><a href="tel:+494037663366">040 37663-366</a></strong
        >.
      </div>
      <div class="gev19-infobox--blue" v-if="!piniaStore.state.isMakler && !aelterAls50">
        <strong>Hinweis:</strong> Sind Sie sich beim Ausfüllen unsicher, rufen Sie uns gern an. Sie erreichen unser
        Beratungsteam unter <strong><a href="tel:+494037663366">040 37663-366</a></strong
        >.
      </div>
    </div>
  </div>
</template>

<style scoped></style>
